import { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  assessmentPhoneLandscapeMedia,
  assessmentShortViewportMedia,
} from "../assessment-layout-styles";

export const containerStyle = (isFinish: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: {
    xs: "column",
    sm: isFinish ? "column" : "row",
    md: isFinish ? "column" : "row",
  },
  alignItems: "center",
  flexShrink: 0,
  minWidth: 0,
});

export const circularContainerStyle: SxProps<Theme> = {
  position: "relative",
  display: "inline-flex",
};

export const circularProgressStyle = (
  remainingSeconds: number,
  totalSeconds: number,
): SxProps<Theme> => {
  const percentLeft =
    totalSeconds > 0 ? (remainingSeconds / totalSeconds) * 100 : 0;
  let strokeColor = "#06B6D4";

  if (percentLeft <= 25) {
    strokeColor = "#EF4444";
  } else if (percentLeft <= 50) {
    strokeColor = "#F59E0B";
  }

  return {
    color: "#06B6D41A",
    "& .MuiCircularProgress-circle": {
      stroke: strokeColor,
    },
  };
};

export const circularProgressOverlayStyle: SxProps<Theme> = {
  position: "absolute",
  color: "#06B6D41A",
  "& .MuiCircularProgress-circle": {
    stroke: "#06B6D41A",
  },
};

export const circularTextContainerStyle = (
  theme: Theme,
  isShortViewport = false,
): SxProps<Theme> => ({
  top: isShortViewport ? "14%" : "10%",
  left: isShortViewport ? "14%" : "10%",
  bottom: isShortViewport ? "14%" : "10%",
  right: isShortViewport ? "14%" : "10%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: alpha(theme.palette.quizPopap.quizBg, 0.94),
  zIndex: 1,
  overflow: "hidden",
});

export const circularTextStyle = (
  isFinish: boolean,
  remainingSeconds: number,
  totalSeconds: number,
  showHoursFormat = false,
  isCompactTablet = false,
  showDaysFormat = false,
  isShortViewport = false,
): SxProps<Theme> => {
  const percentLeft =
    totalSeconds > 0 ? (remainingSeconds / totalSeconds) * 100 : 0;
  let color = "#06B6D4";

  if (percentLeft <= 25) {
    color = "#EF4444";
  } else if (percentLeft <= 50) {
    color = "#F59E0B";
  }

  const fontSize = showDaysFormat
    ? isFinish
      ? "13px"
      : isCompactTablet
        ? "12px"
        : "12px"
    : showHoursFormat
      ? isFinish
        ? "11px"
        : isShortViewport
          ? "7px"
          : isCompactTablet
            ? "10px"
            : "10px"
      : isFinish
        ? "13px"
        : isShortViewport
          ? "9px"
          : isCompactTablet
            ? "11px"
            : "11px";

  return {
    color,
    fontWeight: 700,
    fontSize,
    lineHeight: 1,
    fontFamily: "monospace",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    textAlign: "center",
    letterSpacing: showHoursFormat && isShortViewport ? "-0.04em" : undefined,
    maxWidth: "100%",
    [assessmentPhoneLandscapeMedia]: {
      fontSize: showDaysFormat ? "10px" : showHoursFormat ? "8px" : "9px",
    },
    [assessmentShortViewportMedia]: {
      fontSize: showDaysFormat ? "10px" : showHoursFormat ? "7px" : "9px",
      lineHeight: 1,
    },
  };
};

const getCountdownColor = (
  remainingSeconds: number,
  totalSeconds: number,
): string => {
  const percentLeft =
    totalSeconds > 0 ? (remainingSeconds / totalSeconds) * 100 : 0;

  if (percentLeft <= 25) {
    return "#EF4444";
  }

  if (percentLeft <= 50) {
    return "#F59E0B";
  }

  return "#06B6D4";
};

export const circularDaysNumberStyle = (
  isFinish: boolean,
  remainingSeconds: number,
  totalSeconds: number,
  isCompactTablet = false,
  isShortViewport = false,
): SxProps<Theme> => {
  const color = getCountdownColor(remainingSeconds, totalSeconds);

  return {
    color,
    fontWeight: 700,
    fontSize: isFinish
      ? "18px"
      : isShortViewport
        ? "13px"
        : isCompactTablet
          ? "16px"
          : "17px",
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
    [assessmentPhoneLandscapeMedia]: {
      fontSize: "14px",
    },
    [assessmentShortViewportMedia]: {
      fontSize: "13px",
    },
  };
};

export const circularDaysLabelStyle = (
  isFinish: boolean,
  remainingSeconds: number,
  totalSeconds: number,
  isCompactTablet = false,
  isShortViewport = false,
): SxProps<Theme> => {
  const color = getCountdownColor(remainingSeconds, totalSeconds);

  return {
    color,
    fontWeight: 600,
    fontSize: isFinish
      ? "11px"
      : isShortViewport
        ? "8px"
        : isCompactTablet
          ? "10px"
          : "10px",
    lineHeight: 1,
    mt: "-1px",
    letterSpacing: "0.02em",
    [assessmentPhoneLandscapeMedia]: {
      fontSize: "9px",
    },
    [assessmentShortViewportMedia]: {
      fontSize: "8px",
    },
  };
};

export const resultsContainerStyle = (isFinish: boolean): SxProps<Theme> => ({
  ml: { xs: "0", sm: isFinish ? "0" : "8px", md: "16px" },
  mt: { xs: 0, sm: isFinish ? "16px" : "0", md: isFinish ? "12px" : "0" },
  display: "flex",
  flexDirection: "column",
  alignItems: {
    xs: "center",
    sm: isFinish ? "center" : "flex-start",
    md: "flex-start",
  },
  whiteSpace: { xs: "normal", sm: "nowrap", md: "nowrap" },
  minWidth: 0,
});

export const timeLabelStyle = (
  isFinish: boolean,
  theme: Theme,
  remainingSeconds: number,
  totalSeconds: number,
): SxProps<Theme> => {
  const percentLeft =
    totalSeconds > 0 ? (remainingSeconds / totalSeconds) * 100 : 0;
  let color = isFinish ? theme.palette.quizPopap.quizText : "#06B6D4";

  if (percentLeft <= 25) {
    color = "#EF4444";
  } else if (percentLeft <= 50) {
    color = "#F59E0B";
  }

  return {
    display: { xs: "none", sm: "block" },
    fontWeight: 600,
    color: color,
    fontSize: { xs: "14px", sm: "14px", md: "16px" },
    whiteSpace: { xs: "normal", sm: "nowrap", md: "nowrap" },
  };
};
